import React, {useEffect, useState, useRef} from 'react'
import PropTypes from 'prop-types'
import Slider from 'react-slick'

import {
    Box,
    Heading,
    Stack,
    AspectRatio,
    Skeleton
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {useProductSearch} from '@salesforce/commerce-sdk-react'
import {useSearchParams} from '@salesforce/retail-react-app/app/hooks'
import useIntersectionObserver from '@salesforce/retail-react-app/app/hooks/use-intersection-observer'
import {useWishList} from '../../../hooks/use-wish-list'
import {useCurrentCustomer} from '../../../hooks/use-current-customer'
import {useWishlistActions} from '../../../hooks/use-wishlist-action'
import useSegment from '../../../integrations/segment/app/hooks/use-segment'
import ProductTile from '../../../components/product-tile'
import {BlackArrow} from '../../../components/icons'
import {pdProductCarousalStyles} from '../../../theme/components/project/pdpage-product-recommendation-styles'
import {getActiveSlideProducts} from '../../../integrations/segment/app/utils/segment-utils'

export const CategoryProductTile = ({tileUrl, categoryTitle}) => {
    const scrollRef = useRef()
    const visibleRef = useRef()
    const segment = useSegment()
    const {data: wishListData} = useWishList()
    const {data: customer} = useCurrentCustomer()
    const [productData, setProductData] = useState([])
    const [searchParams] = useSearchParams()
    const {addItemToWishlist, removeItemFromWishlist} = useWishlistActions()
    const [activeSlideProducts, setActiveSlideProducts] = useState([])
    const isRecommenderOnScreen = useIntersectionObserver(visibleRef, {useOnce: true})

    const handleSlideChange = (currentSlide) => {
        const activeProducts = getActiveSlideProducts(currentSlide, productData, scrollRef, '')
        setActiveSlideProducts(activeProducts)
    }

    useEffect(() => {
        if (isRecommenderOnScreen && productData) {
            const timer = setTimeout(() => {
                handleSlideChange()
            }, 100)
            return () => clearTimeout(timer)
        }
    }, [productData, isRecommenderOnScreen])

    useEffect(() => {
        if (isRecommenderOnScreen && productData) {
            if (activeSlideProducts?.activeProducts?.length > 0) {
                activeSlideProducts?.activeProducts.forEach((element) => {
                    element.c_category = productSearchResult?.selectedRefinements?.cgid
                })
                segment.sendProductListViewed(activeSlideProducts)
            }
        }
    }, [isRecommenderOnScreen, activeSlideProducts])

    const handleClick = (onClick) => {
        if (typeof onClick === 'function') {
            onClick()
        }
        setTimeout(handleSlideChange, 100)
    }

    const NextArrow = ({className, onClick}) => (
        <Box
            className={className}
            onClick={() => handleClick(onClick)}
            tabIndex={0}
            role="button"
            onKeyDown={(e) => {
                if (e.key === 'Enter') handleClick(onClick)
            }}
            position="absolute"
            right={'0'}
        >
            <BlackArrow />
        </Box>
    )

    const PrevArrow = ({className, onClick}) => (
        <Box
            className={className}
            onClick={() => handleClick(onClick)}
            tabIndex={0}
            role="button"
            onKeyDown={(e) => {
                if (e.key === 'Enter') handleClick(onClick)
            }}
            transform="rotate(180deg)"
            position="absolute"
            right="105px"
        >
            <BlackArrow />
        </Box>
    )

    const sliderSettings = {
        infinite: true,
        speed: 500,
        slidesToShow: 5,
        slidesToScroll: 1,
        nextArrow: <NextArrow />,
        prevArrow: <PrevArrow />,
        arrows: true,
        responsive: [
            {
                breakpoint: 1200,
                settings: {
                    slidesToShow: 4,
                    slidesToScroll: 2,
                    arrows: true
                }
            },
            {
                breakpoint: 767,
                settings: {
                    slidesToShow: 1.3,
                    slidesToScroll: 1,
                    arrows: false,
                    afterChange: (currentSlide) => {
                        setTimeout(() => handleSlideChange(currentSlide), 100)
                    }
                }
            }
        ]
    }

    if (tileUrl) {
        searchParams._refine.push(`cgid=${tileUrl}`)
    }

    const {data: productSearchResult, isLoading} = useProductSearch({
        parameters: {
            ...searchParams,
            perPricebook: true,
            allVariationProperties: true,
            allImages: true,
            refine: searchParams._refine
        }
    })

    useEffect(() => {
        if (productSearchResult?.hits) {
            setProductData(productSearchResult.hits)
        }
    }, [productSearchResult?.hits])

    return (
        <Box sx={pdProductCarousalStyles} className="carousel-wrapper product-wrapper">
            <Box
                data-test-id="category-product-tile-carousal"
                ref={visibleRef}
                position="relative"
                pb={6}
                className="pd-product-carousal-container"
            >
                <Heading className="recommenderHeading" as={'h2'}>
                    {categoryTitle ?? ''}
                </Heading>

                <Slider {...sliderSettings} ref={scrollRef} className="carousel-sg">
                    {isLoading
                        ? [0, 1, 2, 3].map((key) => (
                              <Box
                                  key={key}
                                  className="product-tile-block"
                                  flex="0 0 auto"
                                  width="275px"
                                  mx={2}
                                  style={{scrollSnapAlign: 'start'}}
                              >
                                  <Stack>
                                      <AspectRatio ratio={1}>
                                          <Skeleton />
                                      </AspectRatio>
                                      <Skeleton height="20px" width="80%" />
                                      <Skeleton height="20px" width="50%" />
                                  </Stack>
                              </Box>
                          ))
                        : productData?.map((product, index) => (
                              <Box key={index} className="product-tile-block">
                                  <ProductTile
                                      product={product}
                                      enableFavourite
                                      productPosition={index}
                                      isFavourite={
                                          customer?.isRegistered
                                              ? wishListData?.registeredUserWishlists?.some(
                                                    (list) =>
                                                        list?.customerProductListItems?.some(
                                                            (item) =>
                                                                item?.productId ===
                                                                (product?.id || product?.productId)
                                                        )
                                                )
                                              : wishListData?.productWishList[0]?.customerProductListItems?.some(
                                                    (item) =>
                                                        item?.productId ===
                                                        (product?.id || product?.productId)
                                                )
                                      }
                                      onFavouriteToggle={(isFavourite, productDetail, event) => {
                                          const action = isFavourite
                                              ? (p, event) =>
                                                    addItemToWishlist(
                                                        p,
                                                        index,
                                                        productDetail,
                                                        event
                                                    )
                                              : (p) =>
                                                    removeItemFromWishlist(p, index, productDetail)
                                          return action(product, event)
                                      }}
                                  />
                              </Box>
                          ))}
                </Slider>
            </Box>
        </Box>
    )
}

CategoryProductTile.propTypes = {
    tileUrl: PropTypes.string,
    categoryTitle: PropTypes.string,
    className: PropTypes.string,
    onClick: PropTypes.func
}

export default CategoryProductTile
